import { motion } from "framer-motion";
import { Wifi, Network, Monitor, RadioTower } from "lucide-react";
import { useLanguage } from "@/i18n";
import SectionHead, { EASE, reveal } from "@/components/SectionHead";

const AERIAL_IMG =
  "https://images.unsplash.com/photo-1727785035050-81b3600e40fa?crop=entropy&cs=srgb&fm=jpg&q=85";

const CARD_META = [
  { icon: Wifi, span: "md:col-span-7", accent: "text-orange-300", bar: "from-orange-400 to-orange-300" },
  { icon: Network, span: "md:col-span-5", accent: "text-orange-300", bar: "from-orange-400 to-sky-400" },
  { icon: Monitor, span: "md:col-span-5", accent: "text-amber-300", bar: "from-amber-400 to-orange-400" },
  { icon: RadioTower, span: "md:col-span-7", accent: "text-amber-300", bar: "from-amber-400 to-amber-300" },
];

export default function Stack() {
  const { t } = useLanguage();

  return (
    <section id="stack" className="relative py-28 sm:py-32" data-testid="stack-section">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.stack.eyebrow} title={t.stack.h2} body={t.stack.body} testid="stack-title" />

        <div className="mt-16 grid gap-6 md:grid-cols-12">
          {t.stack.cards.map((card, i) => {
            const meta = CARD_META[i];
            const Icon = meta.icon;
            return (
              <motion.div
                key={card.name}
                {...reveal}
                transition={{ duration: 0.75, delay: i * 0.1, ease: EASE }}
                data-testid={`stack-card-${i + 1}`}
                className={`group relative overflow-hidden rounded-2xl border border-slate-800/60 bg-slate-900/40 p-8 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-orange-400/40 hover:shadow-[0_16px_50px_rgba(234,88,12,0.14)] ${meta.span} h-full flex flex-col`}
              >
                <span className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${meta.bar} opacity-40 transition-opacity duration-500 group-hover:opacity-100`} />
                <div className="flex items-start justify-between">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/60 ${meta.accent}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {card.tag}
                  </span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                  {card.name}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400 sm:text-base">
                  {card.desc}
                </p>
              </motion.div>
            );
          })}

          <motion.div
            {...reveal}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            data-testid="stack-visual"
            className="relative overflow-hidden rounded-2xl border border-slate-800/60 md:col-span-12 h-64 sm:h-72"
          >
            <img src={AERIAL_IMG} alt="Aerial view of a coastal resort estate" loading="lazy" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" />
            <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300">
                {t.stack.visualTag}
              </span>
              <p className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                {t.stack.visualTitle}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
